class SlHeader extends HTMLElement {
  constructor() {
    super()
    this.isStickyEnabled = this.dataset.sticky === 'true'
    this.isTransparentEnabled = this.dataset.transparent === 'true'
    this.isPanelOpen = false
    this.lockedScrollY = 0
    this.handleScroll = this.handleScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.handlePanelOpen = this.handlePanelOpen.bind(this)
    this.handlePanelClose = this.handlePanelClose.bind(this)
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this)
  }

  connectedCallback() {
    this.topbar = this.querySelector('.m-topbar')
    this.shell = this.querySelector('.sl-header-shell')
    this.desktopBar = this.querySelector('.sl-header__desktop-bar')
    this.mobileBar = this.querySelector('.sl-header__mobile-bar')
    this.mobileDrawer = this.querySelector('[data-sl-mobile-drawer]')
    this.mobileBackdrop = this.querySelector('[data-sl-mobile-backdrop]')
    this.mobilePanel = this.querySelector('[data-sl-mobile-panel]')
    this.mobileToggles = Array.from(this.querySelectorAll('[data-sl-open-mobile]'))
    this.mobileClosers = Array.from(this.querySelectorAll('[data-sl-close-mobile]'))
    this.mobileNavs = Array.from(this.querySelectorAll('sl-header-nav[data-mode="mobile"]'))

    this.mobileToggles.forEach(button => button.addEventListener('click', () => this.openMobileDrawer()))
    this.mobileClosers.forEach(button => button.addEventListener('click', () => this.closeMobileDrawer()))
    this.mobileBackdrop?.addEventListener('click', () => this.closeMobileDrawer())

    this.addEventListener('sl:panel-open', this.handlePanelOpen)
    this.addEventListener('sl:panel-close', this.handlePanelClose)
    document.addEventListener('keydown', this.handleDocumentKeydown)
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.handleResize)

    this.updateMeasurements()
    this.handleScroll()
  }

  disconnectedCallback() {
    this.removeEventListener('sl:panel-open', this.handlePanelOpen)
    this.removeEventListener('sl:panel-close', this.handlePanelClose)
    document.removeEventListener('keydown', this.handleDocumentKeydown)
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
  }

  handlePanelOpen() {
    this.isPanelOpen = true
    this.classList.add('has-active-panel')
    this.handleScroll()
  }

  handlePanelClose() {
    this.isPanelOpen = false
    this.classList.remove('has-active-panel')
    this.handleScroll()
  }

  handleDocumentKeydown(event) {
    if (event.key !== 'Escape') return
    this.closeMobileDrawer()
  }

  handleResize() {
    this.updateMeasurements()
    if (window.innerWidth > 1024) this.closeMobileDrawer()
    this.mobileNavs?.forEach(nav => nav.refreshMobileCarousels?.())
    this.handleScroll()
  }

  updateMeasurements() {
    const topbarHeight = this.topbar ? this.topbar.offsetHeight : 0
    const activeBar = window.innerWidth > 1024 ? this.desktopBar : this.mobileBar
    const barHeight = activeBar ? activeBar.offsetHeight : 0

    this.style.setProperty('--sl-topbar-height', `${topbarHeight}px`)
    this.style.setProperty('--sl-bar-height', `${barHeight}px`)
    this.style.setProperty('--sl-header-height', `${topbarHeight + barHeight}px`)
  }

  handleScroll() {
    const shouldStick = this.isStickyEnabled && window.scrollY > 16
    this.classList.toggle('is-stuck', shouldStick)

    const isFloating = this.isTransparentEnabled && !shouldStick && window.innerWidth > 1024
    const isTransparent = isFloating && !this.isPanelOpen
    this.classList.toggle('is-floating', isFloating)
    this.classList.toggle('is-transparent-active', isTransparent)
  }

  openMobileDrawer() {
    if (!this.mobileDrawer) return
    this.lockedScrollY = window.scrollY || window.pageYOffset || 0
    this.mobileDrawer.hidden = false
    this.classList.add('is-mobile-open')
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${this.lockedScrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'
    this.mobileNavs?.forEach(nav => nav.refreshMobileCarousels?.())
  }

  closeMobileDrawer() {
    if (!this.mobileDrawer) return
    this.mobileDrawer.hidden = true
    this.classList.remove('is-mobile-open')
    document.documentElement.style.overflow = ''
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.left = ''
    document.body.style.right = ''
    document.body.style.width = ''
    window.scrollTo(0, this.lockedScrollY || 0)
    this.mobileNavs.forEach(nav => nav.resetMobile?.())
  }
}

class SlHeaderNav extends HTMLElement {
  constructor() {
    super()
    this.mode = this.dataset.mode || 'desktop'
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.handleDesktopMouseLeave = this.handleDesktopMouseLeave.bind(this)
    this.handleDesktopMouseEnter = this.handleDesktopMouseEnter.bind(this)
    this.desktopCloseTimer = null
  }

  connectedCallback() {
    if (this.mode === 'desktop') {
      this.initDesktop()
      return
    }

    this.initMobile()
  }

  disconnectedCallback() {
    if (this.mode === 'desktop') {
      document.removeEventListener('click', this.handleDocumentClick)
      this.clearDesktopCloseTimer()
    }
  }

  initDesktop() {
    this.rootHeader = this.closest('sl-header')
    this.items = Array.from(this.querySelectorAll('.sl-header-nav__item[data-panel-id]'))
    this.panels = Array.from(this.querySelectorAll('.sl-header-panel'))

    if (!this.items.length || !this.panels.length) return

    this.items.forEach(item => {
      item.addEventListener('mouseenter', () => this.openDesktopPanel(item.dataset.panelId))
      item.addEventListener('focusin', () => this.openDesktopPanel(item.dataset.panelId))
      item.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          this.closeDesktopPanels()
          return
        }

        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        this.openDesktopPanel(item.dataset.panelId)
      })
    })

    this.addEventListener('mouseenter', this.handleDesktopMouseEnter)
    this.addEventListener('mouseleave', this.handleDesktopMouseLeave)

    this.panels.forEach(panel => {
      panel.addEventListener('mouseenter', this.handleDesktopMouseEnter)
      panel.addEventListener('mouseleave', this.handleDesktopMouseLeave)
    })

    this.querySelectorAll('.sl-header-l1-item').forEach(button => {
      const handler = () => this.activateDesktopGroup(button.closest('.sl-header-panel'), button.dataset.level1Id)
      button.addEventListener('mouseenter', handler)
      button.addEventListener('focusin', handler)
    })

    this.querySelectorAll('.sl-header-l2-item').forEach(button => {
      const handler = () => this.activateDesktopLeaf(button.closest('.sl-header-panel'), button.dataset.level2Id)
      button.addEventListener('mouseenter', handler)
      button.addEventListener('focusin', handler)
    })

    document.addEventListener('click', this.handleDocumentClick)
  }

  handleDocumentClick(event) {
    if (this.contains(event.target)) return
    this.closeDesktopPanels()
  }

  handleDesktopMouseEnter() {
    this.clearDesktopCloseTimer()
  }

  handleDesktopMouseLeave(event) {
    const nextTarget = event.relatedTarget
    if (nextTarget && this.contains(nextTarget)) return

    this.clearDesktopCloseTimer()
    this.desktopCloseTimer = window.setTimeout(() => {
      this.closeDesktopPanels()
    }, 120)
  }

  clearDesktopCloseTimer() {
    if (!this.desktopCloseTimer) return
    window.clearTimeout(this.desktopCloseTimer)
    this.desktopCloseTimer = null
  }

  openDesktopPanel(panelId) {
    if (!panelId) return
    this.clearDesktopCloseTimer()

    this.items.forEach(item => {
      const isActive = item.dataset.panelId === panelId
      item.classList.toggle('is-active', isActive)
      const trigger = item.querySelector('.sl-header-nav__trigger')
      if (trigger) trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false')
    })

    this.panels.forEach(panel => {
      const isActive = panel.dataset.panelId === panelId
      panel.classList.toggle('is-open', isActive)
      panel.hidden = !isActive
      if (isActive) this.ensureDesktopDefaults(panel)
    })

    this.rootHeader?.dispatchEvent(new CustomEvent('sl:panel-open', { bubbles: true }))
  }

  closeDesktopPanels() {
    this.clearDesktopCloseTimer()
    this.items?.forEach(item => {
      item.classList.remove('is-active')
      const trigger = item.querySelector('.sl-header-nav__trigger')
      if (trigger) trigger.setAttribute('aria-expanded', 'false')
    })

    this.panels?.forEach(panel => {
      panel.classList.remove('is-open')
      panel.hidden = true
    })

    this.rootHeader?.dispatchEvent(new CustomEvent('sl:panel-close', { bubbles: true }))
  }

  ensureDesktopDefaults(panel) {
    if (!panel || panel.dataset.panelStyle !== 'mega') return

    const activeGroup = panel.querySelector('.sl-header-l1-item.is-active') || panel.querySelector('.sl-header-l1-item')
    if (!activeGroup) return
    this.activateDesktopGroup(panel, activeGroup.dataset.level1Id)
  }

  activateDesktopGroup(panel, level1Id) {
    if (!panel || !level1Id) return

    const groupButtons = Array.from(panel.querySelectorAll('.sl-header-l1-item'))
    const groups = Array.from(panel.querySelectorAll('.sl-header-l2-group'))
    groupButtons.forEach(button => button.classList.toggle('is-active', button.dataset.level1Id === level1Id))
    groups.forEach(group => {
      const isActive = group.dataset.level1Id === level1Id
      group.classList.toggle('is-active', isActive)
      group.hidden = !isActive
    })

    const activeGroup = panel.querySelector(`.sl-header-l2-group[data-level1-id="${level1Id}"]`)
    if (!activeGroup) return
    const activeLeaf = activeGroup.querySelector('.sl-header-l2-item.is-active') || activeGroup.querySelector('.sl-header-l2-item')
    if (!activeLeaf) return
    this.activateDesktopLeaf(panel, activeLeaf.dataset.level2Id)
  }

  activateDesktopLeaf(panel, level2Id) {
    if (!panel || !level2Id) return

    const leafButtons = Array.from(panel.querySelectorAll('.sl-header-l2-item'))
    const panes = Array.from(panel.querySelectorAll('.sl-header-l3-pane'))
    leafButtons.forEach(button => button.classList.toggle('is-active', button.dataset.level2Id === level2Id))
    panes.forEach(pane => {
      const isActive = pane.dataset.level2Id === level2Id
      pane.classList.toggle('is-active', isActive)
      pane.hidden = !isActive
    })
  }

  initMobile() {
    this.screens = Array.from(this.querySelectorAll('.sl-mobile-screen'))
    this.mobileCarousels = Array.from(this.querySelectorAll('[data-sl-mobile-carousel]'))
    this.activeScreenId = 'root'

    this.addEventListener('click', event => {
      const target = event.target.closest('[data-sl-target]')
      if (target) {
        event.preventDefault()
        this.openMobileScreen(target.dataset.slTarget)
        return
      }

      const back = event.target.closest('[data-sl-back]')
      if (back) {
        event.preventDefault()
        this.openMobileScreen(back.dataset.slBack)
        return
      }

      const accordionTrigger = event.target.closest('[data-sl-accordion-trigger]')
      if (accordionTrigger) {
        event.preventDefault()
        this.toggleMobileAccordion(accordionTrigger)
      }
    })

    this.initMobileCarousels()
    this.openMobileScreen(this.activeScreenId)
  }

  resetMobile() {
    this.resetMobileAccordions()
    this.openMobileScreen('root')
  }

  openMobileScreen(screenId) {
    if (!screenId) return
    this.activeScreenId = screenId
    this.screens.forEach(screen => {
      const isActive = screen.dataset.screenId === screenId
      screen.classList.toggle('is-active', isActive)
      screen.hidden = !isActive
    })
    this.resetMobileAccordions()
    requestAnimationFrame(() => this.refreshMobileCarousels())
  }

  toggleMobileAccordion(trigger) {
    const group = trigger.closest('[data-sl-accordion]')
    const content = group?.querySelector('[data-sl-accordion-content]')
    if (!group || !content) return

    const isExpanded = trigger.getAttribute('aria-expanded') === 'true'
    trigger.setAttribute('aria-expanded', isExpanded ? 'false' : 'true')
    group.classList.toggle('is-open', !isExpanded)
    content.hidden = isExpanded
    requestAnimationFrame(() => this.refreshMobileCarousels())
  }

  resetMobileAccordions() {
    this.querySelectorAll('[data-sl-accordion]').forEach(group => {
      group.classList.remove('is-open')
    })

    this.querySelectorAll('[data-sl-accordion-trigger]').forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false')
    })

    this.querySelectorAll('[data-sl-accordion-content]').forEach(content => {
      content.hidden = true
    })
  }

  initMobileCarousels() {
    this.mobileCarousels.forEach(track => {
      if (track.dataset.slCarouselBound === 'true') return
      track.dataset.slCarouselBound = 'true'
      track.addEventListener('scroll', () => this.updateMobileCarousel(track), { passive: true })
      this.updateMobileCarousel(track)
    })
  }

  refreshMobileCarousels() {
    this.mobileCarousels?.forEach(track => this.updateMobileCarousel(track))
  }

  updateMobileCarousel(track) {
    const thumb = track
      .closest('.sl-mobile-card-carousel')
      ?.querySelector('[data-sl-mobile-carousel-thumb]')

    if (!thumb) return

    const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0)

    if (maxScroll <= 1 || track.clientWidth <= 0) {
      thumb.style.width = '100%'
      thumb.style.left = '0%'
      return
    }

    const visibleRatio = track.clientWidth / track.scrollWidth
    const thumbWidth = Math.min(Math.max(visibleRatio * 100, 18), 100)
    const maxOffset = 100 - thumbWidth
    const progress = track.scrollLeft / maxScroll
    const thumbOffset = progress * maxOffset

    thumb.style.width = `${thumbWidth}%`
    thumb.style.left = `${thumbOffset}%`
  }
}

if (!customElements.get('sl-header')) {
  customElements.define('sl-header', SlHeader)
}

if (!customElements.get('sl-header-nav')) {
  customElements.define('sl-header-nav', SlHeaderNav)
}
